import type { FieldComparison } from "./types.ts"

const NOT_SET_LABEL = "Not set"
const LEAD_UNSET_LABEL = "—"

interface ContractDiffRow {
  key: string
  label: string
  /** Which baseline this row was compared against — drives the row's own label. */
  baselineSource: "lead" | "proposal"
  baselineLabel: string
  baselineDisplay: string
  contractDisplay: string
  /** `false` only for `scope` currently — see `FieldComparison.syncable_to_document`. */
  syncableToDocument: boolean
}

/**
 * Client-side diff for the Contract Generate-time confirm modal — compares
 * each field against the *relevant prior source* (the linked Proposal's
 * current value when one exists, the Lead's otherwise), not against the
 * Lead unconditionally the way the Proposal-side comparison does. `has_diff`
 * on the raw `FieldComparison` is always Lead-relative, so it can't be reused
 * here. `sales_owner` is excluded — a different concept by design, never
 * actionable in either Generate modal. `hasLinkedProposal` is the caller's
 * own `document.has_proposal` (`ContractDetailSerializer`, the real, direct
 * field for this) — not inferred from the diff data.
 */
function buildContractGenerateDiffRows(
  fields: FieldComparison[],
  hasLinkedProposal: boolean
): ContractDiffRow[] {
  return fields
    .filter((field) => field.key !== "sales_owner")
    .map((field) => {
      const baselineSource: "lead" | "proposal" = hasLinkedProposal
        ? "proposal"
        : "lead"
      const baselineDisplay = hasLinkedProposal
        ? (field.proposal_display ?? NOT_SET_LABEL)
        : (field.lead_display ?? LEAD_UNSET_LABEL)
      const contractDisplay = field.contract_display ?? NOT_SET_LABEL

      return {
        key: field.key,
        label: field.label,
        baselineSource,
        baselineLabel: hasLinkedProposal ? "Proposal" : "Lead",
        baselineDisplay,
        contractDisplay,
        syncableToDocument: field.syncable_to_document !== false
      }
    })
    .filter((row) => row.baselineDisplay !== row.contractDisplay)
}

export { type ContractDiffRow, buildContractGenerateDiffRows }
